import React, { useEffect, useState, useContext, useMemo } from "react";
import { DataGrid, GridFilterInputDate } from "@mui/x-data-grid";
import { Typography, Box, CircularProgress, Alert, Tooltip, Button, TextField, Autocomplete, Checkbox, Snackbar } from "@mui/material";
import { API_ENDPOINTS } from "../config/api.config";

import { UserContext } from '../components/MiniDrawer';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { useCurrentUser } from '../hooks/useCurrentUser';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import { excludeForecastWorklogs } from '../utils/worklogFilters';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions, 
  Stack, 
  LinearProgress 
} from "@mui/material";


/** Definizione colonne griglia worklog — stabile tra pagine (evita re-mount / flicker). */
function createWorklogGridColumns() {
  return [
    { field: 'project_name', headerName: 'Project', width: 180, minWidth: 100 },
    { field: 'user_name', headerName: 'User', width: 180, minWidth: 100 },
    { field: 'team_name', headerName: 'Team', width: 160, minWidth: 80 },
    { field: 'ticket_id', headerName: 'Ticket ID', width: 140, minWidth: 80 },
    { 
      field: 'date', 
      headerName: 'Date', 
      width: 130,
      minWidth: 90,
      type: 'date',
      filterable: true,
      valueFormatter: (value) => {
        if (!value) return '';
        const date = new Date(value);
        return date.toLocaleDateString('en-GB');
      },
      filterOperators: [
        {
          label: 'Equals',
          value: 'equals',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              return value === filterItem.value;
            };
          },
          InputComponent: GridFilterInputDate,
        },
        {
          label: 'After',
          value: 'after',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              return new Date(value) > new Date(filterItem.value);
            };
          },
          InputComponent: GridFilterInputDate,
        },
        {
          label: 'Before',
          value: 'before',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              return new Date(value) < new Date(filterItem.value);
            };
          },
          InputComponent: GridFilterInputDate,
        },
      ],
    },
    { 
      field: 'effort', 
      headerName: 'Effort (min)', 
      width: 100,
      minWidth: 70,
      valueFormatter: (value) => {
        if (!value) return '';
        return `${Number(value)}`;
      }
    },
    { 
      field: 'effort_hours', 
      headerName: 'Effort (hours)', 
      width: 120,
      minWidth: 80,
      valueGetter: (value, row) => {
        return row.effort;
      },
      valueFormatter: (value) => {
        if (!value) return '';
        const minutes = Number(value);
        const hours = Math.round((minutes / 60) * 100) / 100;
        return `${hours}`;
      }
    },
    { field: 'activity_name', headerName: 'Activity', width: 180, minWidth: 100 },
    { field: 'effort_type_name', headerName: 'Effort Type', width: 140, minWidth: 90 },
    { field: 'description', headerName: 'Description', width: 250, minWidth: 120 },
  ];
}

/** Serializza i filtri colonna DataGrid per l'API worklogs (server-side). */
function serializeGridFilters(filterModel) {
  const items = (filterModel?.items || []).filter((item) => {
    if (!item?.operator) return false;
    if (item.operator === 'isEmpty' || item.operator === 'isNotEmpty') return true;
    if (item.value == null) return false;
    if (typeof item.value === 'string') return item.value.trim() !== '';
    if (Array.isArray(item.value)) return item.value.length > 0;
    return true;
  });
  if (items.length === 0) return null;
  return JSON.stringify(
    items.map(({ field, operator, value }) => ({ field, operator, value }))
  );
}

// Funzione per pulire il ticket_id rimuovendo spazi prima/dopo e tra prefix e resto
function cleanTicketId(ticketId, prefix) {
  if (!ticketId) return '';
  const trimmed = String(ticketId).trim();
  if (!prefix) return trimmed;
  const prefixTrimmed = String(prefix).trim();
  // Se il ticket_id inizia con il prefix, rimuovi gli spazi tra prefix e resto
  if (prefixTrimmed && trimmed.startsWith(prefixTrimmed)) {
    const afterPrefix = trimmed.slice(prefixTrimmed.length).trim();
    return prefixTrimmed + afterPrefix;
  }
  return trimmed;
}



export default function TrackingListManagerView() {
  const { selectedUser } = useContext(UserContext);
  const { isAdmin, isServiceManager, isTeamLeader, isJRServiceManager, isDeliveryManager, currentRole } = useRoleAccess();
  const { userId } = useCurrentUser();
  
  // Fallback: controlla direttamente se il ruolo è 7 (DELIVERY_MANAGER)
  const isDeliveryManagerDirect = currentRole === 7;
  // Bulk Edit Ticket ID: non disponibile per Team Leader (ruolo globale TE_TEAM_LEADER)
  const canUseBulkTicketEdit = !isTeamLeader;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [rowCount, setRowCount] = useState(0);
  const [gridFilterModel, setGridFilterModel] = useState({ items: [] });
  const [gridSortModel, setGridSortModel] = useState([{ field: 'date', sort: 'desc' }]);
  // 2. Rimuovo la funzione buildFormDataFromRow (non più usata)
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  
  // ============================================================================
  // TEAM LEADER FILTERING LOGIC - COMMENTED OUT (TEMPORARY)
  // Questa logica limita il Team Leader a vedere solo i membri del suo team.
  // Attualmente commentata per allineare il comportamento a Service Manager.
  // Per ripristinare: decommenta tutte le sezioni marcate con [TL_FILTER]
  // ============================================================================
  // const [teamLeaderProjectMembers, setTeamLeaderProjectMembers] = useState({});
  // const [teamLeaderMembersLoaded, setTeamLeaderMembersLoaded] = useState(false);
  
  // TEMPORARY: Imposta sempre come loaded (nessun filtraggio team)
  // useMemo per evitare che l'oggetto vuoto venga ricreato ad ogni render (causerebbe loop infinito)
  const teamLeaderProjectMembers = useMemo(() => ({}), []);
  const teamLeaderMembersLoaded = true;
  // Flag per DELIVERY_MANAGER: indica se l'utente ha fatto almeno una ricerca
  const [hasSearched, setHasSearched] = useState(false);

  // --- NUOVI STATI PER BULK EDIT ---
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkSearchData, setBulkSearchData] = useState({
    oldTicketId: '',
    projectId: '', // Progetto selezionato per il filtro ricerca
    newTicketId: '',
    newProjectId: '', // Nuovo progetto da assegnare
    newActivityId: '', // Nuova activity da assegnare
    startDate: '',
    endDate: ''
  });
  
  // Stato per le attività
  const [allActivities, setAllActivities] = useState([]); // Tutte le activity globali
  const [projectActivitiesMap, setProjectActivitiesMap] = useState({}); // Mappa project_id -> activity_ids
  
  // Carica activity all'avvio
  useEffect(() => {
      fetch(API_ENDPOINTS.ACTIVITIES)
        .then(res => res.json())
        .then(data => {
            setAllActivities(data.activities || []);
        })
        .catch(err => console.error("Error loading activities:", err));
  }, []);

  // Carica le activity associate a un progetto quando serve (per il dropdown di modifica)
  const loadProjectActivities = async (projectId) => {
      if (!projectId) return [];
      if (projectActivitiesMap[projectId]) return projectActivitiesMap[projectId];

      try {
          const res = await fetch(`${API_ENDPOINTS.PROJECT_ACTIVITIES}?project_id=${projectId}`);
          if (res.ok) {
              const data = await res.json();
              const activityIds = data.activity_ids || []; // Assumendo che l'endpoint restituisca { activity_ids: [...] }
              // Se l'endpoint non restituisce activity_ids ma associations, mappiamo
              // Aggiorna la mappa
              setProjectActivitiesMap(prev => ({ ...prev, [projectId]: activityIds }));
              return activityIds;
          }
      } catch (e) {
          console.error("Error loading project activities:", e);
      }
      return [];
  };

  const [bulkMatchingRows, setBulkMatchingRows] = useState([]); // I worklog trovati
  const [bulkSelectedRowIds, setBulkSelectedRowIds] = useState([]); // ID selezionati per l'aggiornamento
  const [bulkStep, setBulkStep] = useState(0); // 0: Search, 1: Select, 2: Input New Data, 3: Processing
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, errors: 0 });
  const [bulkFetchLoading, setBulkFetchLoading] = useState(false);
  
  // Snackbar per notifiche
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };
  
  const showNotification = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // Apre la modale inizializzando le date con quelle del filtro attuale
  const handleOpenBulkDialog = () => {
    if (!canUseBulkTicketEdit) return;
    setBulkSearchData({
      oldTicketId: '',
      projectId: '',
      newTicketId: '',
      newProjectId: '',
      newActivityId: '',
      startDate: filterStartDate,
      endDate: filterEndDate
    });
    setBulkMatchingRows([]);
    setBulkSelectedRowIds([]);
    setBulkStep(0);
    setBulkProgress({ current: 0, total: 0, errors: 0 });
    setBulkFetchLoading(false);
    setBulkDialogOpen(true);
  };

  const handleCloseBulkDialog = () => {
    if (bulkStep === 3 && bulkProgress.current < bulkProgress.total) {
      // Impedisci chiusura durante l'elaborazione
      return;
    }
    setBulkFetchLoading(false);
    setBulkDialogOpen(false);
  };

  // Step 1: Trova i worklog (query dedicata server-side: non dipende dalla pagina della griglia)
  const handleFindTicketIds = async () => {
    if (!canUseBulkTicketEdit) return;
    if (!bulkSearchData.oldTicketId || !bulkSearchData.startDate || !bulkSearchData.endDate) {
      showNotification("Please fill in Ticket ID and Date Range.", "warning");
      return;
    }

    setBulkFetchLoading(true);
    try {
      const chunkSize = 1000;
      const maxRows = 25000;
      let skip = 0;
      let total = null;
      const collected = [];

      while (true) {
        const params = buildBulkTicketSearchParams({ limit: chunkSize, skip });
        if (params === null) {
          showNotification(
            'No projects in scope for this search. Adjust main filters or bulk project selection.',
            'warning'
          );
          setBulkMatchingRows([]);
          setBulkSelectedRowIds([]);
          return;
        }

        let url = API_ENDPOINTS.WORKLOGS;
        url += (url.includes('?') ? '&' : '?') + params.join('&');

        const response = await fetch(url);
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        const chunk = excludeForecastWorklogs(data.worklogs || []);
        const totalNumeric = typeof data.total === 'number' ? data.total : null;
        if (totalNumeric != null) {
          total = totalNumeric;
        }

        collected.push(
          ...chunk.map((row, idx) => ({
            id: row.id != null ? row.id : idx,
            ...row,
          }))
        );

        if (chunk.length === 0 || collected.length >= maxRows) {
          break;
        }
        if (totalNumeric != null && collected.length >= totalNumeric) {
          break;
        }
        skip += chunkSize;
      }

      if (typeof total === 'number' && total > collected.length && collected.length >= maxRows) {
        showNotification(
          `Found ${total} matching records; only the first ${maxRows} were loaded. Narrow the date range.`,
          'warning'
        );
      }

      setBulkMatchingRows(collected);
      setBulkSelectedRowIds(collected.map((r) => r.id));

      if (collected.length === 0) {
        showNotification('No records found matching the criteria.', 'info');
      } else {
        setBulkStep(1);
      }
    } catch (err) {
      console.error(err);
      showNotification('Error finding worklogs. Please try again.', 'error');
    } finally {
      setBulkFetchLoading(false);
    }
  };

  // Step 2: Conferma selezione e vai all'input del nuovo ID
  const handleConfirmSelection = () => {
      console.log("Confirmed selection IDs:", bulkSelectedRowIds);
      if (bulkSelectedRowIds.length === 0) {
          showNotification("Please select at least one record to update.", "warning");
          return;
      }
      setBulkStep(2);
  };

  // Step 3: Esegui Replace
  const handleExecuteReplace = async () => {
    if (!canUseBulkTicketEdit) return;
    console.log("Starting replace process. Selected IDs:", bulkSelectedRowIds);
    
    if (!bulkSearchData.newTicketId) {
        showNotification("Please enter the New Ticket ID", "warning");
        return;
    }

    // Filtra solo le righe selezionate
    // FIX: Usa confronto robusto (string vs string) per evitare mismatch di tipo tra DataGrid (spesso string) e dati (number)
    const rowsToUpdate = bulkMatchingRows.filter(row => 
        bulkSelectedRowIds.some(selectedId => String(selectedId) === String(row.id))
    );
    console.log("Rows to update (filtered):", rowsToUpdate);

    if (rowsToUpdate.length === 0) {
        showNotification("No rows selected to update (check selection logic).", "error");
        return;
    }

    setBulkStep(3); // Processing UI
    setBulkProgress({ current: 0, total: rowsToUpdate.length, errors: 0 });

    let updatedCount = 0;
    let errorCount = 0;

    // Elaborazione sequenziale
    for (const row of rowsToUpdate) {
        try {
            // Determina i valori da usare (nuovi se selezionati, altrimenti originali)
            const finalProjectId = bulkSearchData.newProjectId 
                ? bulkSearchData.newProjectId.id 
                : (row.project_id || row.project?.id);
            
            const finalActivityId = bulkSearchData.newActivityId 
                ? bulkSearchData.newActivityId.id 
                : (row.activity_id || row.activity?.id);

            // Trova il progetto finale per ottenere il prefix
            const finalProject = projects.find(p => p.id === finalProjectId);

            // Costruisci un payload che include tutto TRANNE la data (per bypassare il blocco temporale)
            // e include esplicitamente 'id' per il routing del backend.
            const payload = {
                id: row.id,
                ticket_id: cleanTicketId(bulkSearchData.newTicketId, finalProject?.ticket_id_prefix),
                project_id: finalProjectId,
                activity_id: finalActivityId,
                user_id: row.user_id || row.user?.id,
                effort_type_id: row.effort_type_id || row.effort_type?.id,
                effort: row.effort,
                description: row.description || ""
                // DATE ESCLUSA INTENZIONALMENTE
            };

            // MODIFICA CRITICA: Usiamo l'endpoint base invece di quello con ID nel path.
            // Questo spesso risolve problemi CORS se la risorsa /{id} non è configurata esplicitamente su API Gateway.
            // Il backend Lambda legge comunque l'ID dal body (payload.id).
            const res = await fetch(`${API_ENDPOINTS.WORKLOGS}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                let errorInfo = `Status: ${res.status} ${res.statusText}`;
                try {
                    const text = await res.text();
                    if (text) errorInfo += ` - Body: ${text}`;
                } catch (e) {
                    errorInfo += " (Could not read body)";
                }
                throw new Error(errorInfo);
            }
            
            updatedCount++;
        } catch (error) {
            console.error(`Failed to update row ${row.id}`, error);
            // Aggiungi dettagli all'errore per il debug
            console.error("Payload sent:", {
                id: row.id,
                ticket_id: bulkSearchData.newTicketId
            });
            errorCount++;
        }

        // Aggiorna progresso
        setBulkProgress(prev => ({
            ...prev,
            current: prev.current + 1,
            errors: errorCount
        }));
    }

    // Fine elaborazione
    const successMessage = errorCount > 0 
      ? `Process completed with errors. Updated: ${updatedCount}, Errors: ${errorCount}` 
      : `Process completed successfully! Updated ${updatedCount} record${updatedCount !== 1 ? 's' : ''}.`;
    const severity = errorCount > 0 ? 'warning' : 'success';
    
    showNotification(successMessage, severity);
    handleCloseBulkDialog();
    fetchWorklogs(); // Ricarica la tabella principale
  };

  // Helper: verifica se l'utente è TEAM_LEADER per un progetto specifico
  const isTeamLeaderForProject = (project) => {
    if (!project || !project.project_role) return false;
    const roleCode = project.project_role.code || '';
    return roleCode.toUpperCase() === 'T';
  };

  // Helper: verifica se l'utente ha un ruolo manager (SERVICE_MANAGER, etc.) per un progetto specifico
  const isManagerForProject = (project) => {
    if (!project) return false;
    // Se non c'è project_role, considera il progetto come manager (fallback per compatibilità)
    if (!project.project_role) {
      // Se l'utente è ADMIN, considera tutti i progetti come manager
      return isAdmin;
    }
    const roleId = project.project_role.id;
    const roleCode = project.project_role.code || '';
    // Se è Team Leader (role_code 'T'), non è manager per i dati
    if (roleCode.toUpperCase() === 'T') return false;
    // Ruoli manager: 1 (Service Manager), 2 (JR Service Manager)
    return [1, 2].includes(roleId);
  };
  // Filtri per progetto e intervallo date
  const [filterProjects, setFilterProjects] = useState([]);
  // Imposta di default il mese corrente
  function getCurrentMonthRange() {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    // yyyy-mm-dd
    const toStr = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start: toStr(firstDayOfMonth), end: toStr(lastDayOfMonth) };
  }
  const currentMonth = getCurrentMonthRange();
  const [filterStartDate, setFilterStartDate] = useState(currentMonth.start);
  const [filterEndDate, setFilterEndDate] = useState(currentMonth.end);

  // Stato per i filtri "pending" (modificabili dall'utente)
  const [pendingFilterProjects, setPendingFilterProjects] = useState(filterProjects);
  const [pendingFilterStartDate, setPendingFilterStartDate] = useState(filterStartDate);
  const [pendingFilterEndDate, setPendingFilterEndDate] = useState(filterEndDate);

  // Aggiorna i filtri effettivi solo quando si preme il pulsante
  const applyFilters = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setGridFilterModel({ items: [] });
    setGridSortModel([{ field: 'date', sort: 'desc' }]);
    setFilterProjects(pendingFilterProjects);
    setFilterStartDate(pendingFilterStartDate);
    setFilterEndDate(pendingFilterEndDate);
    // Per DELIVERY_MANAGER: segna che l'utente ha fatto almeno una ricerca
    if (isDeliveryManager || isDeliveryManagerDirect) {
      setHasSearched(true);
    }
  };

  const handleMainGridPaginationChange = (model) => {
    setPaginationModel((prev) => ({
      page: model?.page ?? prev.page,
      pageSize: model?.pageSize ?? prev.pageSize,
    }));
  };

  const handleMainGridFilterChange = (model) => {
    setGridFilterModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleMainGridSortChange = (model) => {
    setGridSortModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const buildWorklogQueryParams = ({
    limit,
    skip,
    includePagination = true,
    filterModel = gridFilterModel,
    sortModel = gridSortModel,
  } = {}) => {
    const params = [];
    if (filterStartDate) params.push(`date_from=${encodeURIComponent(filterStartDate)}`);
    if (filterEndDate) params.push(`date_to=${encodeURIComponent(filterEndDate)}`);
    if (includePagination && limit != null) params.push(`limit=${limit}`);
    if (includePagination && skip != null) params.push(`skip=${skip}`);

    const serializedGridFilters = serializeGridFilters(filterModel);
    if (serializedGridFilters) {
      params.push(`grid_filters=${encodeURIComponent(serializedGridFilters)}`);
    }

    const activeSort = sortModel?.find((item) => item?.field && item?.sort);
    if (activeSort) {
      params.push(`sort_field=${encodeURIComponent(activeSort.field)}`);
      params.push(`sort_dir=${encodeURIComponent(activeSort.sort)}`);
    }

    if (isDeliveryManager || isDeliveryManagerDirect) {
      const selectedProjects = filterProjects.length > 0
        ? projects.filter(p => filterProjects.includes(p.name))
        : [];

      if (selectedProjects.length > 0) {
        params.push(`project_ids=${selectedProjects.map(p => p.id).join(',')}`);
      }
      return params;
    }

    const selectedProjects = filterProjects.length > 0
      ? projects.filter(p => filterProjects.includes(p.name))
      : projects;

    const teamLeaderProjects = selectedProjects.filter(p => isTeamLeaderForProject(p));
    const managerProjects = selectedProjects.filter(p => {
      if (isTeamLeaderForProject(p)) return false;
      if (isAdmin) return true;
      return isManagerForProject(p);
    });

    if (isAdmin || isServiceManager || isTeamLeader || isJRServiceManager) {
      const allRoleProjects = [...managerProjects, ...teamLeaderProjects];
      if (allRoleProjects.length > 0) {
        params.push(`project_ids=${allRoleProjects.map(p => p.id).join(',')}`);
      }
    }

    return params;
  };

  /** Scope progetti della ricerca bulk (allineato a buildWorklogQueryParams), senza filtro progetto opzionale nel dialog. */
  const getBulkSearchBaseProjectIds = () => {
    let ids = null;

    if (isDeliveryManager || isDeliveryManagerDirect) {
      const selectedProjects = filterProjects.length > 0
        ? projects.filter(p => filterProjects.includes(p.name))
        : [];
      if (selectedProjects.length > 0) {
        ids = selectedProjects.map(p => p.id);
      } else {
        ids = null;
      }
    } else {
      const selectedProjects = filterProjects.length > 0
        ? projects.filter(p => filterProjects.includes(p.name))
        : projects;

      const teamLeaderProjects = selectedProjects.filter(p => isTeamLeaderForProject(p));
      const managerProjects = selectedProjects.filter(p => {
        if (isTeamLeaderForProject(p)) return false;
        if (isAdmin) return true;
        return isManagerForProject(p);
      });

      if (isAdmin || isServiceManager || isTeamLeader || isJRServiceManager) {
        const allRoleProjects = [...managerProjects, ...teamLeaderProjects];
        ids = allRoleProjects.length > 0 ? allRoleProjects.map(p => p.id) : [];
      } else {
        ids = [];
      }
    }

    return ids;
  };

  /** Project IDs per la query GET worklogs (scope + eventuale progetto scelto nel bulk). */
  const getProjectIdsForBulkTicketSearch = () => {
    const selectedBulkProject = bulkSearchData.projectId;
    const hasBulkProjectFilter =
      selectedBulkProject &&
      typeof selectedBulkProject === 'object' &&
      selectedBulkProject.id != null;
    const bulkPid = hasBulkProjectFilter ? Number(selectedBulkProject.id) : null;

    let ids = getBulkSearchBaseProjectIds();

    if (bulkPid != null && !Number.isNaN(bulkPid)) {
      if (ids === null) {
        ids = [bulkPid];
      } else {
        ids = ids.filter((id) => Number(id) === bulkPid);
      }
    }

    return ids;
  };

  /** Progetti mostrati nel dropdown opzionale del bulk: tutti quelli nello scope (es. include TL), non solo "manager". */
  const getBulkSearchOptionalProjectOptions = () => {
    const ids = getBulkSearchBaseProjectIds();
    if (ids === null) {
      return projects;
    }
    if (ids.length === 0) {
      return [];
    }
    const idSet = new Set(ids.map((id) => Number(id)));
    return projects.filter((p) => idSet.has(Number(p.id)));
  };

  const buildBulkTicketSearchParams = ({ limit, skip }) => {
    const params = [];
    params.push(`date_from=${encodeURIComponent(bulkSearchData.startDate)}`);
    params.push(`date_to=${encodeURIComponent(bulkSearchData.endDate)}`);
    params.push(`ticket_id=${encodeURIComponent(String(bulkSearchData.oldTicketId).trim())}`);
    params.push(`limit=${limit}`);
    params.push(`skip=${skip}`);

    const projectIds = getProjectIdsForBulkTicketSearch();
    if (Array.isArray(projectIds) && projectIds.length === 0) {
      return null;
    }
    if (Array.isArray(projectIds) && projectIds.length > 0) {
      params.push(`project_ids=${projectIds.join(',')}`);
    }
    return params;
  };

  // ============================================================================
  // [TL_FILTER] TEAM LEADER MEMBERSHIP LOADING - COMMENTED OUT
  // Questo useEffect carica i membri dei team per filtrare i worklogs.
  // Attualmente disabilitato per far vedere al TL tutti gli inserimenti.
  // ============================================================================
  /*
  useEffect(() => {
    let cancelled = false;

    const loadTeamLeaderMembers = async () => {
      // OTTIMIZZAZIONE: DELIVERY_MANAGER bypassa completamente il caricamento team
      if (isDeliveryManager || isDeliveryManagerDirect) {
        if (!cancelled) {
          setTeamLeaderProjectMembers({});
          setTeamLeaderMembersLoaded(true);
        }
        return;
      }

      // Se i progetti non sono ancora caricati, non fare nulla (aspetta che arrivino)
      if (projects.length === 0) {
        // NON impostare teamLeaderMembersLoaded = true qui!
        // Lascialo false finché i progetti non sono caricati
        return;
      }
      
      // Se non c'è userId, non possiamo caricare i membri
      if (!userId) {
        if (!cancelled) {
          setTeamLeaderProjectMembers({});
          setTeamLeaderMembersLoaded(true);
        }
        return;
      }

      // Filtra solo i progetti dove l'utente è TEAM_LEADER
      const teamLeaderProjects = projects.filter(p => isTeamLeaderForProject(p));
      
      if (teamLeaderProjects.length === 0) {
        if (!cancelled) {
          setTeamLeaderProjectMembers({});
          setTeamLeaderMembersLoaded(true);
        }
        return;
      }

      setTeamLeaderMembersLoaded(false);
      const membershipMap = {};

      for (const project of teamLeaderProjects) {
        try {
          // Il filtro è ora gestito completamente dal backend, quindi passiamo sempre user_id per i progetti team leader
          const userParam = userId ? `&user_id=${userId}` : '';
          const res = await fetch(`${API_ENDPOINTS.TEAMS}?project_id=${project.id}&include_members=true${userParam}`);
          if (!res.ok) {
            continue;
          }
          const data = await res.json();
          const teams = data.teams || [];
          const memberIdSet = new Set();
          const teamIdSet = new Set();
          const memberTeamMap = {};

          // I team sono già filtrati dal backend (solo quelli di cui l'utente è team leader)
          teams.forEach((team) => {
            const members = Array.isArray(team.members) ? team.members : [];
            teamIdSet.add(team.id);
            members.forEach((member) => {
              const user = member.user || member;
              const memberId = Number(user?.id || member?.user_id);
              if (!memberId) return;
              memberIdSet.add(memberId);
              const teamName = team.name || '';
              if (teamName) {
                if (!memberTeamMap[memberId]) {
                  memberTeamMap[memberId] = new Set();
                }
                memberTeamMap[memberId].add(teamName);
              }
            });
          });

          if (memberIdSet.size > 0) {
            const projectId = Number(project.id);
            membershipMap[projectId] = {
              memberIds: Array.from(memberIdSet),
              memberIdsSet: memberIdSet,
              teamIds: Array.from(teamIdSet),
              memberTeamMap: Object.fromEntries(
                Object.entries(memberTeamMap).map(([key, value]) => [key, Array.from(value)])
              ),
            };
          }
        } catch (error) {
          console.error(`Error loading team membership for project ${project.id}:`, error);
        }
      }

      if (!cancelled) {
        setTeamLeaderProjectMembers(membershipMap);
        setTeamLeaderMembersLoaded(true);
      }
    };

    loadTeamLeaderMembers();

    return () => {
      cancelled = true;
    };
  }, [projects, userId, isDeliveryManager, isDeliveryManagerDirect]);
  */

  // Carica tutti i progetti all'avvio per il filtro
  useEffect(() => {
    if (isDeliveryManager || isDeliveryManagerDirect) {
      // Delivery Manager vede TUTTI i progetti attivi
      fetch(API_ENDPOINTS.PROJECTS)
        .then(res => res.json())
        .then(data => {
          const arr = data.projects || [];
          setProjects(Array.isArray(arr) ? arr : []);
        })
        .catch(() => setProjects([]));
      return;
    }

    if (isAdmin || isServiceManager || isTeamLeader || isJRServiceManager) {
      // Admin, Service Manager, Team Leader, JR Service Manager vedono solo progetti con ruoli 1,2,3
      // Il filtro viene fatto lato backend con role_ids=1,2,3
      console.log('Fetching project users for userId:', userId);
      fetch(`${API_ENDPOINTS.PROJECT_USERS}?user_id=${userId}&role_ids=1,2,3`)
        .then(res => res.json())
        .then(data => {
          console.log('Project users API response:', data);
          if (Array.isArray(data.user_projects_with_references)) {
            // Filtra solo per validity_end_date (il filtro role_ids è già fatto dal backend)
            const currentDate = new Date();
            const validProjectUsers = data.user_projects_with_references.filter(item => {
              if (!item.validity_end_date) return true;
              const endDate = new Date(item.validity_end_date);
              return endDate > currentDate;
            });
            const validProjects = validProjectUsers.map(item => ({
              id: item.project.id,
              name: item.project.name,
              description: item.project.description,
              active: item.project.active,
              project_code: item.project.project_code,
              customer: item.customer,
              project_role: item.project_role // Salva il ruolo dell'utente per questo progetto
            }));
            setProjects(validProjects);
          } else {
            setProjects([]);
          }
        })
        .catch(() => setProjects([]));
    } else {
      // Altri ruoli usano la logica originale
      const url = `${API_ENDPOINTS.PROJECTS}?user_id=${userId}`;
      fetch(url)
        .then(res => res.json())
        .then(data => {
          let arr = data.projects || [];
          setProjects(Array.isArray(arr) ? arr : []);
        })
        .catch(() => setProjects([]));
    }
  }, [isAdmin, isServiceManager, isTeamLeader, isJRServiceManager, isDeliveryManager, isDeliveryManagerDirect, userId]);

  // Imposta automaticamente "Select All" per ruoli autorizzati (ESCLUSO DELIVERY_MANAGER)
  useEffect(() => {
    // DELIVERY_MANAGER escluso: deve selezionare manualmente i progetti per evitare caricamenti massicci
    if ((isAdmin || isServiceManager || isTeamLeader || isJRServiceManager) && projects.length > 0) {
      // Imposta tutti i progetti come selezionati (Select All)
      const allProjectNames = projects.map(p => p.name);
      setPendingFilterProjects(allProjectNames);
      console.log('Auto-setting Select All for authorized projects:', allProjectNames);
    } else if ((isDeliveryManager || isDeliveryManagerDirect) && projects.length > 0) {
      // DELIVERY_MANAGER: inizia con nessun progetto selezionato
      setPendingFilterProjects([]);
      console.log('[DELIVERY_MANAGER] Starting with NO projects selected. User must select manually.');
    }
  }, [projects, isAdmin, isServiceManager, isTeamLeader, isJRServiceManager, isDeliveryManager, isDeliveryManagerDirect]);

  // Carica tutti gli utenti all'avvio
  useEffect(() => {
    fetch(API_ENDPOINTS.USERS)
      .then(res => res.json())
      .then(data => {
        let arr = data.users || [];
        setUsers(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setUsers([]));
  }, []);

  // Aggiorna formData quando users è popolato e c'è una riga in editing
  useEffect(() => {
    // 2. Rimuovo lo stato e le funzioni: openDialog, editingRow, formData, handleOpenDialog, handleCloseDialog, handleFormChange, handleSave
    // 3. Rimuovo la colonna Edit (matita) dalla tabella (actionsColumn, IconButton/EditIcon, ecc.)
    // 4. Rimuovo il Dialog (Dialog, DialogTitle, DialogContent, DialogActions) dal render
  }, [users]);



  // Sposto fetchWorklogs fuori da useEffect
  const fetchWorklogs = async () => {
    setLoading(true);
    setError(null);
    try {
      // Costruisci la query string con filtri e paginazione server-side.
      const params = buildWorklogQueryParams({
        limit: paginationModel.pageSize,
        skip: paginationModel.page * paginationModel.pageSize,
      });
      
      // OTTIMIZZAZIONE: Per DELIVERY_MANAGER, path semplificato (una sola chiamata)
      if (isDeliveryManager || isDeliveryManagerDirect) {
        // Se nessun progetto selezionato, carica TUTTI i worklogs (nessun filtro project_ids)
        
        let url = API_ENDPOINTS.WORKLOGS;
        if (params.length > 0) {
          url += (url.includes('?') ? '&' : '?') + params.join('&');
        }
        
        console.log('[DELIVERY_MANAGER] Single API call:', url);
        const response = await fetch(url);
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Error fetching data: ${response.status} ${response.statusText}. ${errorText || 'No additional details'}`);
        }
        const data = await response.json();
        const allWorklogs = excludeForecastWorklogs(data.worklogs || []);
        setRowCount(data.total || 0);
        
        // OTTIMIZZAZIONE: team_name ora incluso direttamente dalla query SQL (nessuna chiamata API aggiuntiva)
        console.log('[DELIVERY_MANAGER] Worklogs con team_name già incluso:', allWorklogs.length);
        
        setRows(allWorklogs.map((row, idx) => ({ id: row.id || idx, ...row })));
        setLoading(false);
        return;
      }
      
      // PATH STANDARD per altri ruoli (Admin, Service Manager, Team Leader, ecc.)
      // Separa i progetti per ruolo: team leader vs manager
      const selectedProjects = filterProjects.length > 0
        ? projects.filter(p => filterProjects.includes(p.name))
        : projects;

      const teamLeaderProjects = selectedProjects.filter(p => isTeamLeaderForProject(p));
      
      const managerProjects = selectedProjects.filter(p => {
        if (isTeamLeaderForProject(p)) return false; // Escludi se è team leader
        if (isAdmin) return true;
        return isManagerForProject(p);
      });

      let allWorklogs = [];
      
      // ============================================================================
      // [TL_FILTER] TEAM LEADER FETCH AND FILTERING - PARTIALLY COMMENTED OUT
      // La logica di fetch separata per TL è stata commentata.
      // Ora TL e Manager usano la stessa logica (tutti gli inserimenti).
      // ============================================================================
      
      // Fetch worklogs per progetti manager E team leader (una sola chiamata per tutti)
      const allRoleProjects = [...managerProjects, ...teamLeaderProjects];
      if (allRoleProjects.length > 0) {
        let url = API_ENDPOINTS.WORKLOGS;
        if (params.length > 0) {
          url += (url.includes('?') ? '&' : '?') + params.join('&');
        }
        const response = await fetch(url);
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Error fetching data: ${response.status} ${response.statusText}. ${errorText || 'No additional details'}`);
        }
        const data = await response.json();
        allWorklogs = excludeForecastWorklogs(data.worklogs || []);
        setRowCount(data.total || 0);
      } else {
        setRowCount(0);
      }
      
      /*
      // [TL_FILTER] LOGICA ORIGINALE - COMMENTED OUT
      // Fetch worklogs per progetti team leader (una chiamata per progetto con user_id)
      for (const project of teamLeaderProjects) {
        const projectId = Number(project.id);
        const membership = teamLeaderProjectMembers[projectId];
        if (!membership || !membership.memberIdsSet || membership.memberIdsSet.size === 0) {
          continue; // Nessun membro del team, salta questo progetto
        }
        
        // Per ogni membro del team, fai una chiamata separata
        // (il backend WORKLOGS supporta solo un user_id alla volta)
        const memberIds = Array.from(membership.memberIdsSet);
        for (const memberId of memberIds) {
          const memberParams = [...params];
          memberParams.push(`project_ids=${projectId}`);
          memberParams.push(`user_id=${memberId}`);
          
          // Rimuovi project_ids se era già presente (sostituiscilo)
          const filteredParams = memberParams.filter(p => !p.startsWith('project_ids='));
          filteredParams.push(`project_ids=${projectId}`);
          
          let url = API_ENDPOINTS.WORKLOGS;
          if (filteredParams.length > 0) {
            url += (url.includes('?') ? '&' : '?') + filteredParams.join('&');
          }
          
          try {
            const response = await fetch(url);
            if (!response.ok) continue;
            const data = await response.json();
            const worklogs = excludeForecastWorklogs(data.worklogs || []);
            allWorklogs = [...allWorklogs, ...worklogs];
          } catch (error) {
            console.error(`Error fetching worklogs for project ${projectId}, user ${memberId}:`, error);
          }
        }
      }
      */
      
      // OTTIMIZZAZIONE: team_name ora incluso direttamente dalla query SQL del backend
      // Non serve più chiamare addTeamToWorklogs (nessuna chiamata API aggiuntiva)
      console.log('[STANDARD ROLES] Worklogs con team_name già incluso:', allWorklogs.length);

      // ============================================================================
      // [TL_FILTER] WORKLOG FILTERING - COMMENTED OUT
      // La logica di filtraggio per TL è stata commentata.
      // Ora TL vede tutti i worklogs come un Service Manager.
      // ============================================================================
      /*
      // Filtra i worklog per progetti team leader (solo membri del team)
      // I worklog per progetti manager sono già corretti (tutti i worklog del progetto)
      const finalWorklogs = allWorklogs.filter((row) => {
        const projectId = Number(row.project_id || row.project?.id);
        if (!projectId) return true; // Se non c'è project_id, mantieni (non dovrebbe succedere)
        
        const project = projects.find(p => p.id === projectId);
        if (!project) return true; // Progetto non trovato, mantieni
        
        // Se è un progetto team leader, verifica che l'utente sia nel team
        if (isTeamLeaderForProject(project)) {
          const rowUserId = Number(row.user_id || row.user?.id);
          if (!rowUserId) return false;
          
          const membership = teamLeaderProjectMembers[projectId];
          if (!membership) return false;
          
          const allowedSet = membership.memberIdsSet || new Set(
            Array.isArray(membership.memberIds)
              ? membership.memberIds.map((id) => Number(id))
              : []
          );
          return allowedSet.has(rowUserId);
        }
        
        // Se è un progetto manager, mantieni tutti i worklog
        return true;
      });
      */
      
      // TEMPORARY: Nessun filtraggio per TL, mostra tutti i worklogs
      const finalWorklogs = allWorklogs;

      setRows(finalWorklogs.map((row, idx) => ({ id: row.id || idx, ...row })));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // OTTIMIZZAZIONE COMPLETATA: team_name ora incluso direttamente dal backend
  // La funzione addTeamToWorklogs è stata rimossa perché non più necessaria
  // Il backend restituisce già il team_name con un LEFT JOIN su teams nella query SQL

  // Aggiorno la useEffect che richiama fetchWorklogs per dipendere solo dai filtri effettivi
  useEffect(() => {
    if (!selectedUser) return;
    
    // OTTIMIZZAZIONE DELIVERY_MANAGER: Non carica automaticamente, richiede click su "Search"
    if (isDeliveryManager || isDeliveryManagerDirect) {
      // Carica SOLO se l'utente ha cliccato almeno una volta "Search"
      if (hasSearched) {
        fetchWorklogs();
      } else {
        // Non ha ancora fatto una ricerca: mostra messaggio iniziale
        setRows([]);
        setRowCount(0);
      }
      return;
    }
    
    // Per altri ruoli: verifica se ci sono progetti team leader che necessitano di caricamento membri
    const teamLeaderProjects = projects.filter(p => isTeamLeaderForProject(p));
    
    // ============================================================================
    // [TL_FILTER] MEMBERSHIP CHECK - COMMENTED OUT
    // Questa logica verifica che il TL abbia team assegnati prima di procedere.
    // Ora il TL vede tutti gli inserimenti senza questo controllo.
    // ============================================================================
    /*
    if (teamLeaderProjects.length > 0 && !teamLeaderMembersLoaded) {
      return;
    }
    
    // Filtra solo i progetti Team Leader che hanno effettivamente una membership assegnata
    // (l'utente deve essere TL E membro di un team per quel progetto)
    const teamLeaderProjectsWithMembership = teamLeaderProjects.filter(project => {
      const projectId = Number(project.id);
      return teamLeaderProjectMembers[projectId]?.memberIdsSet?.size > 0;
    });
    
    // Log per debug: mostra progetti TL esclusi perché senza team assegnato
    const excludedProjects = teamLeaderProjects.filter(project => {
      const projectId = Number(project.id);
      return !teamLeaderProjectMembers[projectId]?.memberIdsSet?.size;
    });
    if (excludedProjects.length > 0) {
      console.log('[TeamLeader] Progetti esclusi (TL senza team assegnato):', 
        excludedProjects.map(p => p.name));
    }
    
    // Procedi con fetchWorklogs: i progetti TL senza membership verranno ignorati nella fetch
    */
    
    // TEMPORARY: TL non aspetta più il caricamento dei membri, procede direttamente
    fetchWorklogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedUser,
    filterProjects,
    filterStartDate,
    filterEndDate,
    projects,
    isAdmin,
    isServiceManager,
    isTeamLeader,
    isJRServiceManager,
    isDeliveryManager,
    isDeliveryManagerDirect,
    teamLeaderMembersLoaded,
    teamLeaderProjectMembers,
    hasSearched,
    paginationModel.page,
    paginationModel.pageSize,
    gridFilterModel,
    gridSortModel,
  ]);

  // OTTIMIZZAZIONE: Non serve più resettare cache dei team (team_name arriva dal backend)
  // OTTIMIZZAZIONE: Rimosso filtro frontend ridondante - il backend già filtra per project_ids e date
  console.log('Filtri attivi:', filterProjects, filterStartDate, filterEndDate);
  console.log('Righe caricate dal backend:', rows.length);




  // Tooltip per le colonne Description e Project Name
  const columnsWithDescriptionTooltip = useMemo(() => {
    return createWorklogGridColumns().map((col) => {
      if (col.field === 'description' || col.field === 'project_name') {
        return {
          ...col,
          renderCell: (params) => (
            <Tooltip title={params.value || ''} placement="top" arrow>
              <span
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                  width: '100%',
                }}
              >
                {params.value}
              </span>
            </Tooltip>
          ),
        };
      }
      return col;
    });
  }, []);

  // Funzione per esportare la tabella in Excel
  const handleExportExcel = async () => {
    if (exportLoading) return;

    setExportLoading(true);
    try {
      const exportChunkSize = 1000;
      let skip = 0;
      let total = null;
      const exportRows = [];

      do {
        const params = buildWorklogQueryParams({
          limit: exportChunkSize,
          skip,
        });
        let url = API_ENDPOINTS.WORKLOGS;
        if (params.length > 0) {
          url += (url.includes('?') ? '&' : '?') + params.join('&');
        }

        const response = await fetch(url);
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Export failed: ${response.status} ${response.statusText}. ${errorText || 'No additional details'}`);
        }

        const payload = await response.json();
        const chunk = excludeForecastWorklogs(payload.worklogs || []);
        total = payload.total ?? chunk.length;
        exportRows.push(...chunk);
        if (chunk.length === 0) break;
        skip += exportChunkSize;
      } while (exportRows.length < total);

      if (exportRows.length === 0) {
        showNotification('No rows available for export.', 'info');
        return;
      }

      const exportColumns = createWorklogGridColumns();
      const dataToExport = exportRows.map(row => {
        const obj = {};
        exportColumns.forEach(col => {
        if (col.field === 'effort') {
          // Per la colonna effort, mantieni il valore numerico per i calcoli Excel
          obj[col.headerName || col.field] = row[col.field] || 0;
          return;
        }
        if (col.field === 'effort_hours') {
          // Calcola le ore a partire dai minuti (due decimali) - mantieni valore numerico per Excel
          const minutes = Number(row.effort || 0);
          const hours = minutes ? Math.round((minutes / 60) * 100) / 100 : 0;
          obj[col.headerName || col.field] = hours;
          return;
        }
        if (col.field === 'date') {
          // Formatta la data per Excel (YYYY-MM-DD) evitando problemi di timezone
          const dateValue = row[col.field];
          if (dateValue) {
            let formattedDate, year, month;
            
            // Usa la data direttamente se è già in formato YYYY-MM-DD
            if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
              formattedDate = dateValue;
              [year, month] = dateValue.split('-');
            } else {
              // Altrimenti converte evitando problemi di timezone
              const date = new Date(dateValue);
              year = date.getFullYear();
              month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              formattedDate = `${year}-${month}-${day}`;
            }
            
            obj[col.headerName || col.field] = formattedDate;
            // Aggiungi colonne Month e Year
            obj['Month'] = month;
            obj['Year'] = year;
          } else {
            obj[col.headerName || col.field] = '';
            obj['Month'] = '';
            obj['Year'] = '';
          }
          return;
        }
        obj[col.headerName || col.field] = row[col.field];
        });
        
        // Aggiungi Project Code alla fine
        if (row.project_code) {
          obj['Project Code'] = row.project_code;
        } else {
          // Fallback: cerca il project_code dall'array projects usando project_id
          const projectId = row.project_id || row.project?.id;
          const project = projects.find(p => p.id === projectId);
          obj['Project Code'] = project?.project_code || '';
        }
        
        return obj;
      });
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Worklogs');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      
      // Genera il nome del file con l'intervallo di date
      let fileName = 'worklogs';
      
      // Funzione per convertire YYYY-MM-DD in DDMMYYYY
      const formatDateForFileName = (dateStr) => {
        if (!dateStr) return '';
        const [year, month, day] = dateStr.split('-');
        return `${day}${month}${year}`;
      };
      
      // Funzione per pulire il nome del progetto per il nome del file
      const cleanProjectName = (projectName) => {
        if (!projectName) return '';
        return projectName.replace(/[^a-zA-Z0-9]/g, '_'); // Sostituisce caratteri speciali con underscore
      };
      
      const projectName = filterProjects.length === 1 ? cleanProjectName(filterProjects[0]) : 
                         filterProjects.length > 1 ? 'Multiple_Projects' : '';
      
      if (filterStartDate && filterEndDate) {
        const startFormatted = formatDateForFileName(filterStartDate);
        const endFormatted = formatDateForFileName(filterEndDate);
        if (projectName) {
          fileName = `worklogs_${projectName}_${startFormatted}-${endFormatted}`;
        } else {
          fileName = `Worklogs_${startFormatted}-${endFormatted}`;
        }
      } else if (filterStartDate) {
        const startFormatted = formatDateForFileName(filterStartDate);
        if (projectName) {
          fileName = `worklogs_${projectName}_from_${startFormatted}`;
        } else {
          fileName = `Worklogs_from_${startFormatted}`;
        }
      } else if (filterEndDate) {
        const endFormatted = formatDateForFileName(filterEndDate);
        if (projectName) {
          fileName = `worklogs_${projectName}_until_${endFormatted}`;
        } else {
          fileName = `Worklogs_until_${endFormatted}`;
        }
      } else if (projectName) {
        fileName = `worklogs_${projectName}`;
      }
    
      saveAs(blob, `${fileName}.xlsx`);
    } catch (err) {
      showNotification(err.message || 'Export failed. Please try again.', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Box>
      <Box my={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem' }}>All Tracking List</Typography>
          <Box>
             {canUseBulkTicketEdit && (
             <Button 
                variant="contained" 
                color="warning" 
                onClick={handleOpenBulkDialog} 
                startIcon={<FindReplaceIcon />}
                sx={{ mr: 2 }}
             >
                Bulk Edit Tickets
             </Button>
             )}
             <Button
               variant="outlined"
               color="primary"
               onClick={handleExportExcel}
               startIcon={exportLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
               disabled={exportLoading}
             >
               {exportLoading ? 'Exporting...' : 'Excel'}
             </Button>
          </Box>
        </Box>
        {/* Filtri progetto e intervallo date */}
        <Box 
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent={{ xs: 'flex-start', md: 'flex-start' }}
          mb={2}
        >
          <Autocomplete
            multiple
            disableCloseOnSelect={true}
            options={[
              { id: 'all', name: 'All', isSpecial: true },
              ...projects
            ]}
            getOptionLabel={(option) => option.name}
            value={projects.filter(p => pendingFilterProjects.includes(p.name))}
            onChange={(event, newValue) => {
              // Handle special options
              const hasAll = newValue.some(v => v.id === 'all');
              
              if (hasAll) {
                setPendingFilterProjects([]);
              } else {
                setPendingFilterProjects(newValue.map(p => p.name));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Projects"
                placeholder="Search projects..."
                size="small"
                sx={{ minWidth: 300 }}
              />
            )}
            renderOption={(props, option, { selected }) => {
              if (option.isSpecial) {
                return (
                  <Box component="li" {...props}>
                    <Checkbox
                      checked={option.id === 'all' ? pendingFilterProjects.length === 0 : false}
                      sx={{ mr: 1, pointerEvents: 'none' }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: 'italic', color: 'black' }}
                    >
                      {option.name}
                    </Typography>
                  </Box>
                );
              }
              
              return (
                <Box component="li" {...props}>
                  <Checkbox
                    checked={selected}
                    sx={{ mr: 1 }}
                  />
                  <Box>
                    <Typography variant="body2">
                      {option.name}
                    </Typography>
                    {option.customer?.name && (
                      <Typography variant="caption" color="textSecondary">
                        {option.customer.name}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderTags={(value, getTagProps) =>
              value.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  All projects
                </Typography>
              ) : (
                <Typography variant="body2">
                  {value.length} selected
                </Typography>
              )
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="No projects found"
            clearOnBlur={false}
            selectOnFocus
            handleHomeEndKeys
            filterOptions={(options, { inputValue }) => {
              // Always show special options at the top
              const specialOptions = options.filter(opt => opt.isSpecial);
              const regularOptions = options.filter(opt => !opt.isSpecial);
              
              // Filter regular options based on input
              const filteredRegular = regularOptions.filter(option =>
                option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                (option.customer?.name && option.customer.name.toLowerCase().includes(inputValue.toLowerCase()))
              );
              
              return [...specialOptions, ...filteredRegular];
            }}
          />
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={pendingFilterStartDate}
            onChange={e => setPendingFilterStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={pendingFilterEndDate}
            onChange={e => setPendingFilterEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" size="small" color="primary" onClick={applyFilters} startIcon={<FilterListIcon />}>Search</Button>
        </Box>
        {(isDeliveryManager || isDeliveryManagerDirect) && !hasSearched ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              👋 Welcome, Delivery Manager!
            </Typography>
            <Typography variant="body1">
              To view worklogs, select projects from the dropdown above (or leave "All" selected) and click <strong>"Search"</strong>.
            </Typography>
          </Alert>
        ) : (
          <Box
            sx={{
              height: 600,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {error ? (
              <Alert severity="error" sx={{ flexShrink: 0 }}>
                {error}
              </Alert>
            ) : null}
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
              <DataGrid
                disableColumnResize={false}
                loading={loading}
                rows={rows}
                columns={columnsWithDescriptionTooltip}
                paginationModel={paginationModel}
                onPaginationModelChange={handleMainGridPaginationChange}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                pagination
                paginationMode="server"
                rowCount={rowCount}
                hideFooter={false}
                sx={{
                  bgcolor: 'white',
                  height: '100%',
                  '& .MuiDataGrid-main': { outline: 'none' },
                }}
                filterMode="server"
                filterModel={gridFilterModel}
                onFilterModelChange={handleMainGridFilterChange}
                sortingMode="server"
                sortModel={gridSortModel}
                onSortModelChange={handleMainGridSortChange}
                disableColumnFilter={false}
                disableColumnMenu={false}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* --- DIALOG PER BULK EDIT (Team Leader escluso) --- */}
      {canUseBulkTicketEdit && (
      <Dialog open={bulkDialogOpen} onClose={handleCloseBulkDialog} maxWidth="lg" fullWidth>
        <DialogTitle>Bulk Edit Ticket ID</DialogTitle>
        <DialogContent>
          {/* STEP 0: SEARCH */}
          {bulkStep === 0 && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <DialogContentText>
                Search runs on the server (not only the rows visible on the current grid page).
                Uses the date range below, Ticket ID (exact match, trimmed), and the same project scope as the main filters.
                You can optionally restrict further by selecting one Project.
              </DialogContentText>
              <Box display="flex" gap={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  value={bulkSearchData.startDate}
                  onChange={(e) => setBulkSearchData({ ...bulkSearchData, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  fullWidth
                  value={bulkSearchData.endDate}
                  onChange={(e) => setBulkSearchData({ ...bulkSearchData, endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              
              {/* Project Dropdown */}
              <Autocomplete
                options={getBulkSearchOptionalProjectOptions()}
                getOptionLabel={(option) => option.name || ""}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                value={
                  bulkSearchData.projectId && typeof bulkSearchData.projectId === 'object'
                    ? bulkSearchData.projectId
                    : null
                }
                onChange={(event, newValue) => {
                    setBulkSearchData({ ...bulkSearchData, projectId: newValue });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Project (Optional)" placeholder="All Projects" fullWidth />
                )}
              />

              <TextField
                label="Ticket ID to Search"
                value={bulkSearchData.oldTicketId}
                onChange={(e) => setBulkSearchData({ ...bulkSearchData, oldTicketId: e.target.value })}
                placeholder="e.g. SAAMSGIV-123"
                fullWidth
                autoFocus
              />
            </Stack>
          )}

          {/* STEP 1: SELECT ROWS */}
          {bulkStep === 1 && (
            <Box sx={{ height: 400, width: '100%', mt: 1 }}>
                <DialogContentText gutterBottom>
                    Found <strong>{bulkMatchingRows.length}</strong> record{bulkMatchingRows.length !== 1 ? 's' : ''}. Select the ones you want to update.
                </DialogContentText>
                <DataGrid
                    disableColumnResize={false}
                    rows={bulkMatchingRows}
                    columns={[
                        { field: 'date', headerName: 'Date', width: 110, minWidth: 90, valueFormatter: (value) => value ? new Date(value).toLocaleDateString('en-GB') : '' },
                        { field: 'ticket_id', headerName: 'Ticket ID', width: 130, minWidth: 80 },
                        { field: 'project_name', headerName: 'Project', width: 180, minWidth: 100 },
                        { field: 'activity_name', headerName: 'Activity', width: 180, minWidth: 100 },
                        { field: 'user_name', headerName: 'User', width: 150, minWidth: 90 },
                        { field: 'effort_hours', headerName: 'Effort (h)', width: 100, minWidth: 70, valueGetter: (value, row) => {
                            const mins = Number(row.effort || 0);
                            return Math.round((mins / 60) * 100) / 100;
                        }},
                        { field: 'description', headerName: 'Description', width: 200, minWidth: 120 },
                    ]}
                    getRowId={(row) => row.id}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowSelectionModel={bulkSelectedRowIds}
                    onRowSelectionModelChange={(newSelection) => {
                        setBulkSelectedRowIds(newSelection);
                    }}
                    pageSizeOptions={[5, 10, 25]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 5 } },
                    }}
                />
            </Box>
          )}

          {/* STEP 2: INPUT NEW DATA & CONFIRM */}
          {bulkStep === 2 && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Typography variant="h6" color="primary">
                Update {bulkSelectedRowIds.length} Selected Record{bulkSelectedRowIds.length !== 1 ? 's' : ''}
              </Typography>
              
              <TextField
                label="New Ticket ID"
                value={bulkSearchData.newTicketId}
                onChange={(e) => setBulkSearchData({ ...bulkSearchData, newTicketId: e.target.value })}
                placeholder="e.g. SAAMSGIV-456"
                fullWidth
                autoFocus
                helperText="Required. This will replace the Ticket ID for all selected records."
              />

              <Typography variant="subtitle2" sx={{ mt: 2, mb: -1 }}>Advanced Options (Optional)</Typography>
              
              <Autocomplete
                options={projects.filter(p => isManagerForProject(p))}
                getOptionLabel={(option) => option.name || ""}
                value={bulkSearchData.newProjectId || null}
                onChange={async (event, newValue) => {
                    setBulkSearchData({ 
                        ...bulkSearchData, 
                        newProjectId: newValue,
                        newActivityId: null // Reset activity when project changes
                    });
                    if (newValue) {
                        await loadProjectActivities(newValue.id);
                    }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Change Project" placeholder="Keep Original" fullWidth />
                )}
              />

              <Autocomplete
                options={
                    bulkSearchData.newProjectId 
                    ? (() => {
                        const allowedIds = projectActivitiesMap[bulkSearchData.newProjectId.id];
                        // Se allowedIds è undefined, null, o array vuoto, mostra TUTTE le activity
                        if (!allowedIds || (Array.isArray(allowedIds) && allowedIds.length === 0)) {
                            return allActivities;
                        }
                        // Altrimenti filtra per le activity permesse
                        return allActivities.filter(a => allowedIds.includes(a.id));
                    })()
                    : []
                }
                getOptionLabel={(option) => option.name || ""}
                value={bulkSearchData.newActivityId || null}
                onChange={(event, newValue) => {
                    setBulkSearchData({ ...bulkSearchData, newActivityId: newValue });
                }}
                disabled={!bulkSearchData.newProjectId}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Change Activity" 
                    placeholder={!bulkSearchData.newProjectId ? "Select Project first" : "Keep Original"}
                    fullWidth 
                    helperText={!bulkSearchData.newProjectId ? "You must select a Project to change Activity" : ""} 
                  />
                )}
              />

              {/* Warning dinamico in base all'Activity selezionata */}
              {bulkSearchData.newActivityId && (
                <>
                  {bulkSearchData.newActivityId.requires_ticket_id && (
                    <Alert severity="info" icon="ℹ️">
                      <strong>Note:</strong> The selected activity requires a Ticket ID. Only worklogs with a non-empty Ticket ID will be compatible with this activity.
                    </Alert>
                  )}
                  {bulkSearchData.newActivityId.requires_description && (
                    <Alert severity="info" icon="ℹ️">
                      <strong>Note:</strong> The selected activity requires a Description. Only worklogs with a non-empty Description will be compatible with this activity.
                    </Alert>
                  )}
                </>
              )}

              <Alert severity="warning">
                <strong>Warning:</strong> This action is irreversible and will update {bulkSelectedRowIds.length} record{bulkSelectedRowIds.length !== 1 ? 's' : ''}, bypassing date restrictions.
              </Alert>
            </Stack>
          )}

          {/* STEP 3: PROCESSING */}
          {bulkStep === 3 && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                Updating records... <strong>{bulkProgress.current}</strong> / <strong>{bulkProgress.total}</strong>
              </Typography>
              <LinearProgress
                variant="determinate"
                value={bulkProgress.total > 0 ? (bulkProgress.current / bulkProgress.total) * 100 : 0}
                sx={{ height: 8, borderRadius: 4 }}
              />
              {bulkProgress.errors > 0 && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  ⚠️ Errors encountered: {bulkProgress.errors}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {bulkStep !== 3 && (
            <Button onClick={handleCloseBulkDialog} color="secondary">Cancel</Button>
          )}
          {bulkStep === 0 && (
            <Button
              onClick={handleFindTicketIds}
              variant="contained"
              color="primary"
              disabled={bulkFetchLoading}
            >
              {bulkFetchLoading ? 'Searching…' : 'Search'}
            </Button>
          )}
          {bulkStep === 1 && (
            <Button onClick={handleConfirmSelection} variant="contained" color="primary">Continue</Button>
          )}
          {bulkStep === 2 && (
            <Button onClick={handleExecuteReplace} variant="contained" color="error">Confirm & Update</Button>
          )}
        </DialogActions>
      </Dialog>
      )}

      {/* Snackbar per notifiche */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
} 